import React, { useRef } from 'react';
import {
  ListBase,
  ListToolbar,
  Datagrid,
  DateField,
  Pagination,
  Title,
  useRecordContext,
  useRefresh,
} from 'react-admin';

import Chip from '@material-ui/core/Chip';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';

import ActionsList from '../../Components/ReactAdmin/ActionsList';
import Textofield from '../../Components/ReactAdmin/Fields/TextoField';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { formatDateDDMMYYYY, PRIMARY_COLOR } from '../../utils';
import { PERMISSAO_TELA_SAIDAS_ENDERECOS_EXPOSICAO_LISTAR } from '../../utils/dadosTelas';
import DialogoAdicionarProduto from '../EnderecoExposicaoMercadorias/DialogoAdicionarProduto';
import AndarField from './AndarField';
import DialogoCriarSaida from './DialogoCriarSaida';
import DialogoEscolherAndarEndereco from './DialogoEscolherAndarEndereco';
import Filter from './Filter';

function getLabelTipo(tipo) {
  return tipo === 'entrada' ? 'Entrada' : 'Saída';
}

const LIMITE_VALIDADES_VISIVEIS = 2;

const ValidadesField = (props) => {
  const record = useRecordContext(props);
  const base = record && record.record ? record.record : record;
  const validades =
    (base && base.andarProduto && base.andarProduto.validades) || [];

  if (validades.length === 0) {
    return <Typography>-</Typography>;
  }

  const datas = validades.map((validade) =>
    formatDateDDMMYYYY(validade.validade),
  );
  const visiveis = datas.slice(0, LIMITE_VALIDADES_VISIVEIS);
  const restantes = datas.slice(LIMITE_VALIDADES_VISIVEIS);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {visiveis.map((data, index) => (
        <Chip key={`${data}-${index}`} size="small" label={data} />
      ))}
      {restantes.length > 0 ? (
        <Tooltip title={restantes.join(', ')} placement="bottom">
          <Chip size="small" label={`+${restantes.length}`} />
        </Tooltip>
      ) : null}
    </div>
  );
};

const rowStyle = (record) => {
  if (!record) return {};
  if (record.tipo === 'entrada') {
    return { backgroundColor: 'rgba(76, 175, 80, 0.15)' };
  }
  if (record.tipo === 'saida') {
    return { backgroundColor: 'rgba(244, 67, 54, 0.15)' };
  }
  return {};
};

const useStylesDatagrid = makeStyles((theme) => ({
  dataHeader: {
    flex: 1,
    backgroundColor: PRIMARY_COLOR,
    padding: '10px',
    color: 'white',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    borderSpacing: 0,
    borderCollapse: 'collapse',
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
      color: 'red',
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
      color: 'red !important',
    },
  },
  dataCell: {
    flex: 1,
    padding: '10px',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
}));

const DataGridLocal = (props) => {
  const classes = useStylesDatagrid();
  return (
    <Datagrid {...props} bulkActionButtons={false} rowStyle={rowStyle}>
      <Textofield
        source="tipo"
        label="Tipo"
        sortable={false}
        headerClassName={classes.dataHeader}
        cellClassName={classes.dataCell}
        formatador={(tipo) => getLabelTipo(tipo)}
      />
      <Textofield
        source="andarProduto.produto.nome"
        label="Produto"
        sortable={false}
        headerClassName={classes.dataHeader}
        cellClassName={classes.dataCell}
      />
      <Textofield
        source="andarProduto.endereco.codigo"
        label="Endereço"
        sortable={false}
        headerClassName={classes.dataHeader}
        cellClassName={classes.dataCell}
      />
      <AndarField
        label="Andar"
        sortable={false}
        headerClassName={classes.dataHeader}
        cellClassName={classes.dataCell}
      />
      <ValidadesField
        label="Validades"
        sortable={false}
        headerClassName={classes.dataHeader}
        cellClassName={classes.dataCell}
      />
      <DateField
        source="data"
        label="Data"
        showTime
        sortable={false}
        headerClassName={classes.dataHeader}
        cellClassName={classes.dataCell}
      />
      <Textofield
        source="user.username"
        label="Usuário"
        sortable={false}
        headerClassName={classes.dataHeader}
        cellClassName={classes.dataCell}
      />
    </Datagrid>
  );
};

const MyList = ({ ...props }) => {
  const refDialogoCriarSaida = useRef(null);
  const refDialogoEscolherAndarEndereco = useRef(null);
  const refDialogoAdicionarProduto = useRef(null);
  const refresh = useRefresh();

  const handleCriar = () => {
    if (refDialogoCriarSaida.current) {
      refDialogoCriarSaida.current.handleOpen();
    }
  };

  const handleRegistrarEntrada = () => {
    if (refDialogoEscolherAndarEndereco.current) {
      refDialogoEscolherAndarEndereco.current.handleOpen();
    }
  };

  const handleContinuarEntrada = (contexto) => {
    if (refDialogoAdicionarProduto.current) {
      refDialogoAdicionarProduto.current.handleOpen(contexto);
    }
  };

  return (
    <>
      <Title title="Mov. Endereços" />
      <ListToolbar
        filters={props.filters}
        actions={
          <ActionsList
            actions={[
              {
                label: 'Entrada',
                icon: <GetAppOutlinedIcon />,
                onClick: handleRegistrarEntrada,
              },
              {
                label: 'Saída',
                icon: <AddIcon />,
                onClick: handleCriar,
              },
            ]}
          />
        }
        {...props}
      />
      <Paper
        elevation={1}
        style={{
          borderRadius: '20px',
          marginTop: '30px',
          display: 'flex',
          padding: '10px',
        }}
      >
        <DataGridLocal {...props} />
      </Paper>
      <Pagination rowsPerPageOptions={[10, 20, 50, 100]} />
      <DialogoCriarSaida ref={refDialogoCriarSaida} handleClose={refresh} />
      <DialogoEscolherAndarEndereco
        ref={refDialogoEscolherAndarEndereco}
        handleContinuar={handleContinuarEntrada}
      />
      <DialogoAdicionarProduto
        ref={refDialogoAdicionarProduto}
        handleClose={refresh}
      />
    </>
  );
};

const ContainerMyList = ({ ...props }) => (
  <ListBase {...props}>
    <MyList {...props} />
  </ListBase>
);

const SaidasList = (props) => (
  <ProtegerPagina permissao={PERMISSAO_TELA_SAIDAS_ENDERECOS_EXPOSICAO_LISTAR}>
    <ContainerMyList
      {...props}
      filters={<Filter />}
      title="Mov. Endereços"
      component="div"
      bulkActionButtons={false}
      sort={{ field: 'data', order: 'desc' }}
      perPage={20}
    />
  </ProtegerPagina>
);

export default SaidasList;
